import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminSettingsApi, type ValideurPaiements } from '../services/api';
import { formatApiError } from '../lib/api-error';
import { useToast } from '../context/ToastContext';
import { Spinner } from '../components/shared/Spinner';

/**
 * Réglages du module Paiements (admin) : la date minimale des factures à payer
 * remontées de Zoho, et la liste des valideurs. Rangés dans Paiements et non
 * dans Utilisateurs : ce sont des réglages financiers (Valérie, 16/10/2026).
 */
export function PaiementsParametresPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const [billDateMin, setBillDateMin] = useState('');

  const q = useQuery({
    queryKey: ['admin', 'settings'],
    queryFn: () => adminSettingsApi.get(),
    staleTime: 60_000,
  });

  useEffect(() => {
    if (q.data) setBillDateMin(q.data.paiements_bill_date_min);
  }, [q.data]);

  const mutation = useMutation({
    mutationFn: () => adminSettingsApi.update({ paiements_bill_date_min: billDateMin }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'settings'] });
      toast.success('Paramètres enregistrés.');
    },
    onError: (err) => toast.error(`Enregistrement échoué : ${formatApiError(err)}`),
  });

  const dirty = q.data && billDateMin !== q.data.paiements_bill_date_min;
  const valid = /^\d{4}-\d{2}-\d{2}$/.test(billDateMin);

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Paramètres des paiements</h1>
        <p className="text-sm text-slate-500 mt-1">Ce qui est synchronisé depuis Zoho, et qui valide les demandes.</p>
      </div>

      {q.isLoading ? (
        <p className="text-slate-500">Chargement…</p>
      ) : q.isError ? (
        <p className="text-[#C0423F]">Erreur : {formatApiError(q.error)}</p>
      ) : (
        <div className="bg-white rounded-lg border border-slate-200 p-5 space-y-4">
          <div>
            <h2 className="font-medium">Paiements : date minimale de synchronisation</h2>
            <p className="text-sm text-slate-500 mt-1">
              Date minimale des <strong>factures à payer</strong> ET des <strong>avoirs à
              rembourser</strong> remontés de Zoho. Tout ce qui est antérieur (vieilles
              factures/avoirs déjà réglés) est ignoré à la synchro.
            </p>
          </div>
          <label className="flex flex-col gap-1 text-sm max-w-xs">
            <span className="text-slate-600">Ne synchroniser qu'à partir du</span>
            <input
              type="date"
              value={billDateMin}
              onChange={(e) => setBillDateMin(e.target.value)}
              className="rounded-md border border-slate-300 px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </label>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => mutation.mutate()}
              disabled={!dirty || !valid || mutation.isPending}
              className="px-3 py-1.5 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-2"
            >
              {mutation.isPending && <Spinner />}
              Enregistrer
            </button>
            {dirty && <span className="text-xs text-[#926B00]">Modifications non enregistrées</span>}
          </div>
        </div>
      )}

      <ValideursSection />
    </div>
  );
}

/**
 * Qui peut valider les paiements fournisseurs et remboursements d'avoirs. Le
 * premier de la liste est proposé par défaut à la soumission.
 *
 * Le rôle admin PRIME sur cette liste (règle de Valérie du 14/10/2026, le soir
 * même, qui a remplacé la règle inverse) : un administrateur voit dans
 * « À valider » TOUTES les demandes et peut statuer sur celles des autres, pour
 * qu'une absence ou une urgence ne bloque pas un paiement — le valideur
 * attribué reçoit alors un courriel disant qui a décidé à sa place. Seul un
 * valideur NON administrateur reste cantonné aux demandes qui lui sont
 * attribuées. Ne pas « rétablir » le cloisonnement entre administrateurs : il a
 * disparu volontairement.
 */
function ValideursSection() {
  const qc = useQueryClient();
  const toast = useToast();
  const [liste, setListe] = useState<ValideurPaiements[]>([]);
  const [ajout, setAjout] = useState('');

  const q = useQuery({
    queryKey: ['admin', 'settings', 'valideurs'],
    queryFn: () => adminSettingsApi.valideurs(),
    staleTime: 60_000,
  });
  useEffect(() => {
    if (q.data) setListe(q.data.valideurs);
  }, [q.data]);

  const mutation = useMutation({
    mutationFn: () => adminSettingsApi.majValideurs(liste),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'settings', 'valideurs'] });
      qc.invalidateQueries({ queryKey: ['paiements-valideurs'] });
      toast.success('Valideurs enregistrés.');
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  const candidats = (q.data?.candidats ?? []).filter((c) => !liste.some((v) => v.email === c.email));
  const dirty = q.data && JSON.stringify(liste) !== JSON.stringify(q.data.valideurs);
  const valide = liste.length > 0 && liste.every((v) => v.nom.trim());

  function ajouter() {
    const c = candidats.find((x) => x.email === ajout);
    if (!c) return;
    // Le prénom suffit dans les courriels (« Bonjour Fabien ») ; modifiable.
    setListe([...liste, { email: c.email, nom: c.nom.split(' ')[0] || c.nom }]);
    setAjout('');
  }

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-5 space-y-4">
      <div>
        <h2 className="font-medium">Paiements : valideurs</h2>
        <p className="text-sm text-slate-500 mt-1">
          Personnes à qui soumettre les paiements fournisseurs et remboursements d'avoirs. Le{' '}
          <strong>premier</strong> est proposé par défaut ; le choix se change à la soumission,
          pour tout, par section ou ligne par ligne. Un administrateur voit et peut traiter{' '}
          <strong>toutes</strong> les demandes, y compris celles attribuées à un autre : le
          valideur attribué reçoit alors un courriel lui disant qui a décidé à sa place. Un
          valideur non administrateur ne voit que les demandes qui lui sont attribuées.
        </p>
      </div>

      {q.isLoading ? (
        <p className="text-slate-500 text-sm">Chargement…</p>
      ) : q.isError ? (
        <p className="text-[#C0423F] text-sm">Erreur : {formatApiError(q.error)}</p>
      ) : (
        <>
          <ul className="divide-y divide-slate-100 rounded-md border border-slate-200">
            {liste.map((v, i) => (
              <li key={v.email} className="flex flex-wrap items-center gap-3 px-3 py-2 text-sm">
                <input
                  value={v.nom}
                  onChange={(e) =>
                    setListe(liste.map((x, j) => (j === i ? { ...x, nom: e.target.value } : x)))
                  }
                  aria-label={`Nom affiché de ${v.email}`}
                  className="w-32 rounded border border-slate-300 px-2 py-1 text-sm text-[#384872] focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <span className="text-slate-500">{v.email}</span>
                {i === 0 ? (
                  <span className="rounded bg-[#7CBF94]/20 px-1.5 py-0.5 text-xs text-[#1E7A3C]">Par défaut</span>
                ) : (
                  <button
                    type="button"
                    onClick={() => setListe([v, ...liste.filter((_, j) => j !== i)])}
                    className="text-xs text-blue-700 hover:underline"
                  >
                    Mettre par défaut
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setListe(liste.filter((_, j) => j !== i))}
                  disabled={liste.length === 1}
                  title={liste.length === 1 ? 'Il faut au moins un valideur' : 'Retirer de la liste'}
                  className="ml-auto text-xs text-[#C0423F] hover:underline disabled:opacity-40 disabled:no-underline"
                >
                  Retirer
                </button>
              </li>
            ))}
          </ul>

          <div className="flex flex-wrap items-center gap-2 text-sm">
            <select
              value={ajout}
              onChange={(e) => setAjout(e.target.value)}
              aria-label="Utilisateur à ajouter comme valideur"
              className="rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Ajouter un valideur…</option>
              {candidats.map((c) => (
                <option key={c.email} value={c.email}>
                  {c.nom} ({c.email})
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={ajouter}
              disabled={!ajout}
              className="px-3 py-1.5 rounded-md text-sm text-slate-700 ring-1 ring-slate-300 hover:bg-slate-50 disabled:opacity-50"
            >
              Ajouter
            </button>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => mutation.mutate()}
              disabled={!dirty || !valide || mutation.isPending}
              className="px-3 py-1.5 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-2"
            >
              {mutation.isPending && <Spinner />}
              Enregistrer les valideurs
            </button>
            {dirty && <span className="text-xs text-[#926B00]">Modifications non enregistrées</span>}
          </div>
        </>
      )}
    </div>
  );
}
